import { InteractionStatus } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { AxiosError, InternalAxiosRequestConfig } from 'axios'
import * as React from 'react'

import { apiClient, ideasbankApiClient, pg3ApiClient } from '../api'
import { getAccessToken } from '../auth/accessToken'
import Loader from '../components/Loader'

interface ApiProviderProps {
  children: React.ReactNode
}

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
}

const clients = [apiClient, ideasbankApiClient, pg3ApiClient]

const applyAuthorizationHeader = (config: InternalAxiosRequestConfig, token: string): InternalAxiosRequestConfig => {
  config.headers = {
    ...(config.headers ?? {}),
    Authorization: `Bearer ${token}`,
  } as InternalAxiosRequestConfig['headers']
  return config
}

export const ApiProvider = ({ children }: ApiProviderProps) => {
  const { instance, inProgress } = useMsal()

  React.useEffect(() => {
    const cleanupHandlers: Array<() => void> = []

    const setupInterceptors = () => {
      for (const client of clients) {
        const requestInterceptorId = client.client.interceptors.request.use(async config => {
          try {
            const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0]

            if (!account) {
              return config
            }

            const token = await getAccessToken(instance, account)

            if (!token) {
              return config
            }

            return applyAuthorizationHeader(config, token)
          } catch (err) {
            console.error(err)
            return config
          }
        })

        const responseInterceptorId = client.client.interceptors.response.use(
          response => response,
          async error => {
            const axiosError = error as AxiosError

            const originalRequest = axiosError.config as RetryableRequestConfig

            if (!originalRequest || originalRequest._retry || axiosError.response?.status !== 401) {
              return Promise.reject(error)
            }

            try {
              const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0]

              if (!account) {
                return Promise.reject(error)
              }

              const token = await getAccessToken(instance, account)

              if (!token) {
                return Promise.reject(error)
              }

              originalRequest._retry = true

              applyAuthorizationHeader(originalRequest, token)

              return client.client.request(originalRequest)
            } catch {
              return Promise.reject(error)
            }
          },
        )

        cleanupHandlers.push(() => client.client.interceptors.request.eject(requestInterceptorId))

        cleanupHandlers.push(() => client.client.interceptors.response.eject(responseInterceptorId))
      }
    }

    setupInterceptors()

    return () => {
      cleanupHandlers.forEach(c => c())
    }
  }, [instance])

  if (inProgress === InteractionStatus.Startup) {
    return React.createElement(Loader, {
      message: 'Preparing application...',
      subMessage: '',
    })
  }
  return React.createElement(React.Fragment, null, children)
}

export default ApiProvider
